import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { PriorityTask } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, SxProps, Theme } from '@mui/material';

import { TaskCard } from './TaskCard';
import { UnassignedTasksEmptyState } from './UnassignedTasksEmptyState';
import { useAgentContext } from './UnassignedTasksSection.agentContext';
import { SectionHeader } from '../atoms/SectionHeader';
import { TASK_CARD_MIN_HEIGHT } from '../constants/priorities.constants';
import { useGetTaskActionsAndProperties } from '../hooks/tasks.hooks';
import { getPlaybookTaskPath } from '../utils/playlist.utils';
import { filterTasks, getPopoverFilterCategories, TaskPopoverFilters } from '../utils/tasks.utils';

const UNASSIGNED_TASKS_SECTION_STRINGS = appStrings.modules.priorities.unassignedTasksSection;

type UnassignedTasksSectionProps = Readonly<{
  unassignedTasks: PriorityTask[];
}>;

export const UnassignedTasksSection = ({ unassignedTasks }: UnassignedTasksSectionProps) => {
  useAgentContext(unassignedTasks);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<TaskPopoverFilters[]>([]);

  const {
    taskTypes,
    taskStatuses,
    deleteTask,
    handleOpenEditTaskDialog,
    updateTask,
    handleOpenNewTaskDialog,
  } = useGetTaskActionsAndProperties();

  const handleEditUnassignedTask = (task: PriorityTask) => {
    handleOpenEditTaskDialog(task, false);
  };

  const handleSettingFilter = (newFilter: TaskPopoverFilters) => {
    setFilters((prev) => {
      const doesIncomingFilterAlreadyExist = prev.some(
        (existingFilter) =>
          existingFilter.status === newFilter.status && existingFilter.type === newFilter.type,
      );

      if (doesIncomingFilterAlreadyExist) {
        // then we remove it
        return prev.filter(
          (existingFilter) =>
            existingFilter.status !== newFilter.status || existingFilter.type !== newFilter.type,
        );
      }

      // otherwise we add it
      return [...prev, newFilter];
    });
  };

  const hasActiveFilters = filters.some(
    (filter) => filter.status !== undefined || filter.type !== undefined,
  );

  const tasksToDisplay = filterTasks({
    unassignedTasks,
    filters,
    taskStatuses,
    taskTypes,
    searchTerm,
  });

  return (
    <Box sx={containerSx}>
      <SectionHeader
        title={UNASSIGNED_TASKS_SECTION_STRINGS.title}
        description={UNASSIGNED_TASKS_SECTION_STRINGS.description}
        searchPlaceholder={UNASSIGNED_TASKS_SECTION_STRINGS.searchTasksPlaceholder}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        hasActiveFilters={hasActiveFilters}
        popoverFilterCategories={getPopoverFilterCategories({
          taskStatuses,
          taskTypes,
          filters,
          handleSettingFilter,
        })}
        clearFilters={() => setFilters([])}
        isFilteredResultEmpty={tasksToDisplay.length === 0 && unassignedTasks.length > 0}
        disabled={unassignedTasks.length === 0}
        inline
      />

      <Box sx={tasksContainerSx}>
        {unassignedTasks.length > 0 &&
          tasksToDisplay.length > 0 &&
          tasksToDisplay.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              deleteTask={deleteTask}
              taskTypes={taskTypes}
              taskStatuses={taskStatuses}
              openEditTaskDialog={handleEditUnassignedTask}
              onOpen={(openedTask) => navigate(getPlaybookTaskPath(openedTask.id))}
              handleDuplicateTask={() => handleOpenNewTaskDialog(null, true, task)}
              updateTask={async (updatedTask) => {
                await updateTask({ ...updatedTask, id: task.id });
              }}
            />
          ))}

        {unassignedTasks.length === 0 && <UnassignedTasksEmptyState />}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  maxWidth: '1240px',
  margin: '0 auto',
  width: '100%',
  px: backrs.spacing['spacing-none'],
  mt: backrs.spacing['spacing-none'],
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-3xl'],
    mt: backrs.spacing['spacing-2xl'],
  },
});

const tasksContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: backrs.spacing['spacing-2xl'],
  minHeight: `${TASK_CARD_MIN_HEIGHT}px`,
});
